/**
 * QuestionCard - Main Question Display Component
 * 
 * Displays the current question with header, content, options, and controls.
 * This is the main container component for quiz participation.
 * 
 * Features:
 * - Question text with category/difficulty badges
 * - Image/media support
 * - Code block support
 * - Answer options
 * - Submit button
 * - Explanation display
 */

import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { AnswerOptionList } from './AnswerOption';
import CodeBlock from '../../codeBlock';
import TerminalOutput from '../../terminalOutput';
import { SmartTextRenderer, EnhancedCodeBlock } from '../../ui/MathRenderer';
import { QuestionMedia } from '../../ui/QuestionMedia';

/**
 * QuestionCard component
 * @param {Object} question - The question object
 * @param {number} questionIndex - Current question index
 * @param {number} totalQuestions - Total number of questions
 * @param {Array} selectedOptions - Currently selected option indices
 * @param {boolean} submitted - Whether answer has been submitted
 * @param {boolean} showCorrectAnswer - Whether to show correct answer
 * @param {boolean} answersLocked - Whether answers are locked
 * @param {Array} hiddenOptions - Options hidden by 50:50 joker
 * @param {Object} audienceResults - Audience joker vote percentages
 * @param {Array} bestPlayerChoices - Best player joker selections
 * @param {Function} onSelect - Option selection handler
 * @param {Function} onSubmit - Submit button handler
 * @param {boolean} showSubmitButton - Whether to show submit button
 * @param {string} submitLabel - Label for submit button
 */
export function QuestionCard({
  question,
  questionIndex = 0,
  totalQuestions = 1,
  selectedOptions = [],
  submitted = false,
  showCorrectAnswer = false,
  answersLocked = false,
  hiddenOptions = [],
  audienceResults = null,
  bestPlayerChoices = [],
  onSelect,
  onSubmit,
  showSubmitButton = true,
  submitLabel = 'Antworten',
}) {
  const { darkMode } = useTheme();

  if (!question) {
    return (
      <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Keine Frage verfügbar
      </div>
    );
  }

  // Get correct answer indices
  const correctIndices = question.correctAnswerIndices || 
    [question.correctAnswerIndex];

  // Check if multiple selection is allowed
  const allowMultiple = question.allowMultiple || correctIndices.length > 1;

  // Disabled state
  const isDisabled = submitted || showCorrectAnswer || answersLocked;

  return (
    <div
      className={`
        rounded-2xl shadow-xl overflow-hidden
        ${darkMode ? 'bg-gray-800' : 'bg-white'}
      `}
    >
      {/* Question Header */}
      <div
        className={`
          p-4 
          ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}
          border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}
        `}
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Question number */}
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Frage {questionIndex + 1} von {totalQuestions}
          </span>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {/* Category badge */}
            {question.category && (
              <span
                className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}
                `}
              >
                {question.category}
              </span>
            )}

            {/* Difficulty badge */}
            {question.difficulty && (
              <span
                className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${question.difficulty === 'hard' || question.difficulty === 'schwer'
                    ? darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
                    : question.difficulty === 'medium' || question.difficulty === 'mittel'
                      ? darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                      : darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                  }
                `}
              >
                {question.difficulty}
              </span>
            )}

            {/* Points badge */}
            {question.points && (
              <span
                className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}
                `}
              >
                {question.points} Pkt.
              </span>
            )}

            {/* Multiple choice indicator */}
            {allowMultiple && (
              <span
                className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}
                `}
              >
                Mehrfachauswahl
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="p-6">
        {/* Question text */}
        <h2 className={`text-xl md:text-2xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <SmartTextRenderer text={question.question || question.text} />
        </h2>

        {/* Legacy code block (old format) */}
        {question.showCodeBlock && question.code && (
          <div className="mb-6">
            <CodeBlock code={question.code} />
          </div>
        )}

        {/* New codeBlock format with LaTeX support */}
        {question.codeBlock && question.codeBlock.code && (
          <div className="mb-6">
            <EnhancedCodeBlock 
              code={question.codeBlock.code} 
              language={question.codeBlock.language} 
            />
          </div>
        )}

        {/* Media/Image */}
        {question.media && (
          <div className="mb-6">
            <QuestionMedia media={question.media} />
          </div>
        )}

        {/* Legacy image URL (old format) */}
        {!question.media && question.imageUrl && (
          <div className="mb-6">
            <img
              src={question.imageUrl}
              alt="Frage-Bild"
              className="max-w-full h-auto rounded-lg mx-auto max-h-64 object-contain"
            />
          </div>
        )}

        {/* Terminal Output */}
        {question.showTerminal && question.terminalOutput && (
          <div className="mb-6">
            <TerminalOutput output={question.terminalOutput} />
          </div>
        )}

        {/* Answer options */}
        <AnswerOptionList
          options={question.options || []}
          selectedOptions={selectedOptions}
          correctIndices={showCorrectAnswer ? correctIndices : []}
          showResult={showCorrectAnswer && submitted}
          hiddenOptions={hiddenOptions}
          audienceResults={audienceResults}
          bestPlayerChoices={bestPlayerChoices}
          allowMultiple={allowMultiple}
          disabled={isDisabled}
          onSelect={onSelect}
        />

        {/* Multiple choice hint - don't reveal how many are correct */}
        {allowMultiple && !showCorrectAnswer && (
          <p className={`mt-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            💡 Mehrfachauswahl möglich
          </p>
        )}

        {/* Submit button */}
        {showSubmitButton && !showCorrectAnswer && !answersLocked && (
          <div className="mt-6">
            <button
              onClick={onSubmit}
              disabled={selectedOptions.length === 0 || submitted}
              className={`
                w-full py-3 px-6 rounded-xl font-medium text-lg
                transition-all duration-200
                ${selectedOptions.length === 0 || submitted
                  ? darkMode 
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98]'
                }
              `}
            >
              {submitted ? '✓ Abgegeben' : submitLabel}
            </button>
          </div>
        )}

        {/* Explanation */}
        {showCorrectAnswer && submitted && question.explanation && (
          <div
            className={`
              mt-6 p-4 rounded-xl
              ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}
              border
            `}
          >
            <h3 className={`font-medium mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
              Erklärung
            </h3>
            <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
              <SmartTextRenderer text={question.explanation} />
            </div>
          </div>
        )}

        {/* Result message */}
        {showCorrectAnswer && submitted && (
          <div
            className={`
              mt-4 p-4 rounded-xl text-center
              ${selectedOptions.length > 0 && 
                correctIndices.every(idx => selectedOptions.includes(idx)) &&
                selectedOptions.every(idx => correctIndices.includes(idx))
                  ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                  : darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
              }
            `}
          >
            {selectedOptions.length > 0 && 
             correctIndices.every(idx => selectedOptions.includes(idx)) &&
             selectedOptions.every(idx => correctIndices.includes(idx))
              ? '✓ Richtig!'
              : selectedOptions.length === 0
                ? '⏰ Keine Antwort ausgewählt'
                : '✗ Leider falsch'
            }
          </div>
        )}
        
        {/* Not submitted message */}
        {showCorrectAnswer && !submitted && selectedOptions.length > 0 && (
          <div
            className={`
              mt-4 p-4 rounded-xl text-center
              ${darkMode ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-gray-100 text-gray-600 border-gray-300'}
              border
            `}
          >
            ⚠️ Antwort nicht abgesendet
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact question card for review mode
 */
export function QuestionCardCompact({
  question,
  index,
  selectedAnswer,
  isCorrect,
}) {
  const { darkMode } = useTheme();

  // Check if answer was provided
  const hasAnswer = selectedAnswer !== null && selectedAnswer !== undefined && 
    (!Array.isArray(selectedAnswer) || selectedAnswer.length > 0);

  // Format the answer display for multiple choice
  const formatAnswer = () => {
    if (!hasAnswer) return null;
    if (Array.isArray(selectedAnswer)) {
      // Multiple choice - show all selected options
      return selectedAnswer.map(i => OPTION_LETTERS[i] || `${i}`).join(', ');
    }
    return OPTION_LETTERS[selectedAnswer] || selectedAnswer;
  };

  return (
    <div
      className={`
        p-4 rounded-lg border
        ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className={`
          w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
          ${isCorrect
            ? 'bg-green-500 text-white'
            : !hasAnswer
              ? darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
              : 'bg-red-500 text-white'
          }
        `}>
          {isCorrect ? '✓' : !hasAnswer ? '-' : '✗'}
        </div>

        {/* Question preview */}
        <div className="flex-grow min-w-0">
          <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} truncate`}>
            {index + 1}. {question.question || question.text}
          </p>
          {hasAnswer && (
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Antwort: {formatAnswer()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

export default QuestionCard;
